import { Component, HostListener, OnDestroy, OnInit } from '@angular/core'
import { Subscription } from 'rxjs'
import { ThemeColorDefinition, brandThemeColors, supportingThemeColors } from '../services/api/models/configuration.model'
import { BrandEditorService, ColorSource } from '../services/ui/brand-editor.service'
import { BrandSelectionService, Selection } from '../services/ui/brand-selection.service'
import { ThemeMode } from '../services/ui/brand-theme.resolver'
import { PreviewStateOffer, PreviewStateService } from '../services/ui/preview-state.service'

/** Which colour field has its picker open, if any. */
interface OpenPicker { scope: 'theme' | 'element', key: string, mode: ThemeMode }

@Component({
    selector: 'app-brand-panel',
    templateUrl: './brand-panel.component.html',
    styleUrls: ['./brand-panel.component.scss'],
})
export class BrandPanelComponent implements OnInit, OnDestroy {

    picking = false
    selection: Selection = null
    themeOpen = false
    supportingOpen = false
    statesOpen = false

    readonly modes: ThemeMode[] = ['light', 'dark']
    readonly themeColors: ThemeColorDefinition[]

    /** The few a brand decides, and the rest that follow from them. */
    readonly brandColors = brandThemeColors()
    readonly supportingColors = supportingThemeColors()

    /** Names offered in the dropdowns, plus the custom option. */
    readonly themeNames: string[]

    openPicker: OpenPicker = null

    /** Whatever the screen in front of you offers, so the list is never out of date. */
    states: PreviewStateOffer[] = []

    isStateOn(key: string): boolean {
        return this.previewState.is(key)
    }

    constructor(
        private editor: BrandEditorService,
        private selectionService: BrandSelectionService,
        private previewState: PreviewStateService,
    ) {
        this.themeColors = this.editor.themeColors
        this.themeNames = this.themeColors.map(t => t.name)
    }

    toggleState(key: string): void {
        this.previewState.toggle(key)
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.selectionService.picking.subscribe(picking => this.picking = picking),
            this.selectionService.selection.subscribe(selection => {
                this.selection = selection
                this.openPicker = null
                this.openDropdown = null
            }),
            // Follows navigation on its own, because the service drops the offer on leaving.
            this.previewState.offered.subscribe(states => this.states = states),
        )

        document.addEventListener('click', this.closeDropdownOnOutsideClick, { capture: true })
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe())
        document.removeEventListener('click', this.closeDropdownOnOutsideClick, { capture: true })
    }

    togglePicking(): void {
        this.selectionService.togglePicking()
    }

    clearSelection(): void {
        this.selectionService.clear()
    }

    // ---- element colours ----

    sourceOf(key: string, mode: ThemeMode): ColorSource {
        return this.editor.sourceOf(key, mode)
    }

    /** Value of the dropdown: a theme name, or the custom marker. */
    selectedOption(key: string, mode: ThemeMode): string {
        return this.sourceOf(key, mode).reference ?? CUSTOM
    }

    async chooseOption(key: string, mode: ThemeMode, option: string): Promise<void> {
        if (option === CUSTOM) {
            // Starts from the colour already showing, so switching to custom changes nothing yet.
            await this.editor.setCustom(key, mode, this.sourceOf(key, mode).value)
            this.openPicker = { scope: 'element', key, mode }
            return
        }

        this.openPicker = null
        await this.editor.setReference(key, mode, option)
    }

    async setElementColor(key: string, mode: ThemeMode, value: string): Promise<void> {
        if (value) await this.editor.setCustom(key, mode, value)
    }

    async resetElement(key: string, mode: ThemeMode): Promise<void> {
        this.openPicker = null
        await this.editor.reset(key, mode)
    }

    /** A reset only makes sense where this mode actually stores something of its own. */
    canReset(key: string, mode: ThemeMode): boolean {
        return this.sourceOf(key, mode).hasOwnValue
    }

    /** Dark with no value of its own just mirrors light, and the row should say so. */
    mirrorsLight(key: string, mode: ThemeMode): boolean {
        return mode === 'dark' && !this.sourceOf(key, mode).hasOwnValue
    }

    // ---- theme colours ----

    themeValue(name: string, mode: ThemeMode): string {
        return this.editor.themeValue(name, mode)
    }

    themeIsInherited(name: string, mode: ThemeMode): boolean {
        return mode === 'dark' && !this.editor.storedThemeValue(name, 'dark')
    }

    async setThemeColor(name: string, mode: ThemeMode, value: string): Promise<void> {
        if (value) await this.editor.setThemeColor(name, mode, value)
    }

    async clearThemeDark(name: string): Promise<void> {
        this.openPicker = null
        await this.editor.setThemeColor(name, 'dark', '')
    }

    // ---- theme dropdown ----

    /** Colour shown next to a theme name in the list, for the mode being edited. */
    optionSwatch(name: string, mode: ThemeMode): string {
        return this.editor.themeValue(name, mode)
    }

    /** Label of the option currently in effect, so the closed trigger reads as a choice. */
    optionLabel(key: string, mode: ThemeMode): string {
        const option = this.selectedOption(key, mode)

        return option === CUSTOM
            ? 'commons.themeConfiguration.inspector.custom'
            : `commons.themeConfiguration.theme.${option}`
    }

    toggleDropdown(key: string, mode: ThemeMode): void {
        this.openDropdown = this.dropdownIsOpen(key, mode) ? null : { key, mode }
    }

    dropdownIsOpen(key: string, mode: ThemeMode): boolean {
        return this.openDropdown?.key === key && this.openDropdown?.mode === mode
    }

    async pickOption(key: string, mode: ThemeMode, option: string): Promise<void> {
        this.openDropdown = null
        await this.chooseOption(key, mode, option)
    }

    openDropdown: { key: string, mode: ThemeMode } = null

    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.openDropdown) this.openDropdown = null
        else if (this.openPicker) this.openPicker = null
    }

    // ---- picker plumbing ----

    toggle(scope: 'theme' | 'element', key: string, mode: ThemeMode): void {
        this.openPicker = this.isOpen(scope, key, mode) ? null : { scope, key, mode }
    }

    isOpen(scope: 'theme' | 'element', key: string, mode: ThemeMode): boolean {
        return this.openPicker?.scope === scope
            && this.openPicker?.key === key
            && this.openPicker?.mode === mode
    }

    readonly custom = CUSTOM

    private subscriptions: Subscription[] = []

    private readonly closeDropdownOnOutsideClick = (event: MouseEvent): void => {
        if (!this.openDropdown) return

        const target = event.target as Element
        if (target?.closest('.theme-dropdown')) return

        this.openDropdown = null
    }
}

const CUSTOM = '__custom__'
